import React, { useState, useCallback } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import { safeFetchAll, MONTH_NAMES } from "./constants";
import { premisesApiResponse } from "../../types/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// ─── Types ────────────────────────────────────────────────────────────────────

interface AuditRecord {
  category: string;
  totalNC: number | null;
  completionNC: number | null;
  pending: number | null;
  completionPercent: number | null;
  currentScore: number | null;
  maxScore: number;
}

interface FireSafetyParameter {
  parameter: string;
  inPercent: number | null;
  score: number | null;
  maxScore: number;
  percentage: number | null;
}

interface ElectricalSafetyItem {
  description: string;
  complianceStatus: string | null;
  score: number;
  maxScore: number;
  remarks: string | null;
}

interface ElectricalSafetySection {
  name: string;
  items: ElectricalSafetyItem[];
  totalScore: number;
  totalMaxScore: number;
  percentage: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Safe folder name: replace chars not allowed in zip entry names
const safeName = (s: string) =>
  (s || "unknown").replace(/[\\/:*?"<>|]/g, "_").trim() || "unknown";

// Pick the most-recent record for a premise in a given month
const pickRecordForMonth = (
  records: any[],
  premiseId: string,
  month: number,
  year: number
): any | null => {
  const filtered = records.filter((r) => {
    // premise match
    const candidates = [
      r.premisesId, r.premise, r.premiseId,
      r.premisesId?._id, r.premise?._id,
    ]
      .filter(Boolean)
      .map(String);
    const matchesPremise =
      candidates.length === 0 || candidates.includes(String(premiseId));
    if (!matchesPremise) return false;

    // month/year match
    const dateStr = r.date || r.createdAt || "";
    if (!dateStr) return false;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return false;
    return d.getMonth() + 1 === month && d.getFullYear() === year;
  });
  if (!filtered.length) return null;
  // sort descending by date — newest first
  filtered.sort((a, b) => {
    const da = new Date(a.date || a.createdAt || 0).getTime();
    const db = new Date(b.date || b.createdAt || 0).getTime();
    return db - da;
  });
  return filtered[0];
};

// ─── PDF colour helpers ───────────────────────────────────────────────────────

const TEAL: [number, number, number] = [0, 168, 165];
const ORANGE: [number, number, number] = [255, 100, 0];
const PURPLE: [number, number, number] = [140, 0, 180];
const BLUE: [number, number, number] = [0, 100, 180];
const GREEN: [number, number, number] = [21, 128, 61];
const GREY_TEXT: [number, number, number] = [80, 80, 80];

function sectionBand(
  doc: jsPDF,
  label: string,
  y: number,
  color: [number, number, number]
): number {
  const pageW = doc.internal.pageSize.width;
  doc.setFillColor(...color);
  doc.rect(14, y, pageW - 28, 8, "F");
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  doc.text(label, 16, y + 6);
  doc.setTextColor(0, 0, 0);
  return y + 10;
}

function pageHeader(
  doc: jsPDF,
  premiseName: string,
  reportTitle: string,
  month: number,
  year: number
) {
  const pageW = doc.internal.pageSize.width;
  doc.setFillColor(...TEAL);
  doc.rect(0, 0, pageW, 16, "F");
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(premiseName, 8, 11);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const sub = `${reportTitle}  ·  ${MONTH_NAMES[month - 1]} ${year}`;
  doc.text(sub, pageW - doc.getTextWidth(sub) - 8, 11);
  doc.setTextColor(0, 0, 0);
}

// ─── MIS Report PDF ───────────────────────────────────────────────────────────

function generateMISPdf(
  mis: any,
  premiseName: string,
  month: number,
  year: number
): Uint8Array {
  const doc = new jsPDF({ orientation: "landscape" });
  pageHeader(doc, premiseName, "Fire Safety MIS Report", month, year);

  let y = 22;

  // Header info
  doc.setFontSize(10);
  doc.setTextColor(...GREY_TEXT);
  doc.text(`Report: ${mis.name || "N/A"}`, 14, y);
  y += 6;
  doc.text(`Date: ${mis.date || "N/A"}    Premises: ${premiseName}`, 14, y);
  y += 8;

  // Fire license
  if (mis.fireLicenseDate || mis.fireNocIssueDate || mis.dateOfRenewal) {
    y = sectionBand(doc, "FIRE LICENSE STATUS", y, [21, 128, 61]);
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    const licInfo = [
      ["Fire License Date", mis.fireLicenseDate || "N/A"],
      ["Fire NOC Issue Date", mis.fireNocIssueDate || "N/A"],
      ["Date of Renewal", mis.dateOfRenewal || "N/A"],
      ["Status Validity", mis.statusAvailability || "N/A"],
    ];
    autoTable(doc, {
      startY: y,
      body: licInfo,
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 50 } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // External audit
  const extAudit: AuditRecord[] = mis.externalAuditRecords || [];
  if (extAudit.length) {
    y = sectionBand(doc, "EXTERNAL AUDIT — % OF NON COMPLIANCE STATUS REPORT", y, ORANGE);
    autoTable(doc, {
      startY: y,
      head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Current Score", "Max Score"]],
      body: extAudit.map((r) => [
        `Category ${r.category}`,
        r.totalNC ?? "N/A",
        r.completionNC ?? "N/A",
        r.pending ?? "N/A",
        r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A",
        r.currentScore != null ? r.currentScore.toFixed(2) : "N/A",
        r.maxScore,
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: ORANGE },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Internal audit
  const intAudit: AuditRecord[] = mis.internalAuditRecords || [];
  if (intAudit.length) {
    y = sectionBand(doc, "INTERNAL AUDIT — % OF NON COMPLIANCE STATUS REPORT", y, PURPLE);
    autoTable(doc, {
      startY: y,
      head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Current Score", "Max Score"]],
      body: intAudit.map((r) => [
        `Category ${r.category}`,
        r.totalNC ?? "N/A",
        r.completionNC ?? "N/A",
        r.pending ?? "N/A",
        r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A",
        r.currentScore != null ? r.currentScore.toFixed(2) : "N/A",
        r.maxScore,
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: PURPLE },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Table I — Fire and Life Safety Equipment
  const flsSystems: any[] = mis.fireAndLifeSafetyEquipment || mis.flsSafetySystems || [];
  if (flsSystems.length) {
    doc.addPage();
    pageHeader(doc, premiseName, "Fire Safety MIS — Table I", month, year);
    y = 22;
    y = sectionBand(doc, "(I) FIRE AND LIFE SAFETY EQUIPMENT", y, BLUE);
    autoTable(doc, {
      startY: y,
      head: [["Sr.No", "Particulars", "Installed", "Working/Healthy", "Not Working", "Last Inspection Date", "Remarks"]],
      body: flsSystems.map((r) => [
        r.srNo,
        r.particulars || "",
        r.installed ?? "N/A",
        r.working ?? "N/A",
        r.notWorking ?? "N/A",
        r.lastInspectionDate || "N/A",
        r.remarks || "—",
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: BLUE },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 80 }, 6: { cellWidth: 40 } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Table II — Fire and Life Safety System
  const mepFLS: any[] = mis.fireAndLifeSafetySystem || mis.mepFLSInfrastructure || [];
  if (mepFLS.length) {
    doc.addPage();
    pageHeader(doc, premiseName, "Fire Safety MIS — Table II", month, year);
    y = 22;
    y = sectionBand(doc, "(II) FIRE AND LIFE SAFETY SYSTEM", y, GREEN);
    autoTable(doc, {
      startY: y,
      head: [["Sr.No", "Systems", "Installed", "Working", "Process", "Status", "Completion Date / Remark"]],
      body: mepFLS.map((r) => [
        r.srNo,
        r.systems || "",
        r.totalInstalledNos ?? "N/A",
        r.workingNos ?? "N/A",
        r.process || "",
        r.status || "N/A",
        r.completionDateOrRemark || "—",
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: GREEN },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 30 }, 4: { cellWidth: 70 }, 6: { cellWidth: 40 } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Table III — Fire Incident / Drills
  const drills: any[] = mis.fireIncidentDrills || [];
  if (drills.length) {
    if (y > doc.internal.pageSize.height - 60) { doc.addPage(); pageHeader(doc, premiseName, "Fire Safety MIS — Table III", month, year); y = 22; }
    y = sectionBand(doc, "(III) FIRE INCIDENT / ACCIDENTS, DRILLS & TRAINING PROGRAMME", y, [124, 58, 237]);
    autoTable(doc, {
      startY: y,
      head: [["Sr.No", "Description", "Actual No.", "Target No.", "Status", "Completion Date / Remark"]],
      body: drills.map((r) => [
        r.srNo,
        r.systems || "",
        r.actualNo ?? "N/A",
        r.targetNo ?? "N/A",
        r.status || "N/A",
        r.completionDateOrRemark || "—",
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [124, 58, 237] },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 100 }, 5: { cellWidth: 40 } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Table IV — ERT Safety Equipment
  const ert: any[] = mis.ertSafetyEquipment || [];
  if (ert.length) {
    doc.addPage();
    pageHeader(doc, premiseName, "Fire Safety MIS — Table IV", month, year);
    y = 22;
    y = sectionBand(doc, "(IV) ERT SAFETY EQUIPMENT STATUS", y, [234, 88, 12]);
    autoTable(doc, {
      startY: y,
      head: [["Sr.No", "Type of Equipment", "Responsible Department", "Total Qty", "Condition", "Last Inspection Date", "Remarks"]],
      body: ert.map((r) => [
        r.srNo,
        r.typeOfEquipment || "",
        r.responsibleDepartment || "",
        r.totalQuantity ?? "N/A",
        r.condition || "N/A",
        r.lastInspectionDate || "N/A",
        r.remarks || "—",
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [234, 88, 12] },
      columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 60 } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Table V — Incident Summary
  const summary: any[] = mis.incidentSummary || [];
  if (summary.length) {
    if (y > doc.internal.pageSize.height - 60) { doc.addPage(); pageHeader(doc, premiseName, "Fire Safety MIS — Table V", month, year); y = 22; }
    y = sectionBand(doc, "(V) INCIDENT SUMMARY — FOR THE MONTH (YEARLY)", y, [72, 176, 212]);
    autoTable(doc, {
      startY: y,
      head: [["Description", "Count", "Remarks"]],
      body: summary.map((r) => [r.description || "", r.count ?? "", r.remarks || ""]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [72, 176, 212] },
    });
  }

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ─── Fire Safety Scorecard PDF ────────────────────────────────────────────────

function generateFireScorecardPdf(
  scorecard: any,
  premiseName: string,
  month: number,
  year: number
): Uint8Array {
  const doc = new jsPDF({ orientation: "landscape" });
  pageHeader(doc, premiseName, "Fire Safety Scorecard", month, year);

  let y = 22;
  doc.setFontSize(10);
  doc.setTextColor(...GREY_TEXT);
  doc.text(`Name: ${scorecard.name || "N/A"}    Date: ${scorecard.date || "N/A"}    Premises: ${premiseName}`, 14, y);
  y += 8;

  // External audit
  const extAudit: AuditRecord[] = scorecard.externalAuditRecords || [];
  if (extAudit.length) {
    y = sectionBand(doc, "EXTERNAL AUDIT — % OF NON COMPLIANCE STATUS REPORT", y, ORANGE);
    autoTable(doc, {
      startY: y,
      head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Current Score", "Max Score"]],
      body: extAudit.map((r) => [
        `Category ${r.category}`,
        r.totalNC ?? "N/A",
        r.completionNC ?? "N/A",
        r.pending ?? "N/A",
        r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A",
        r.currentScore != null ? r.currentScore.toFixed(2) : "N/A",
        r.maxScore,
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: ORANGE },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Internal audit
  const intAudit: AuditRecord[] = scorecard.internalAuditRecords || [];
  if (intAudit.length) {
    y = sectionBand(doc, "INTERNAL AUDIT — % OF NON COMPLIANCE STATUS REPORT", y, PURPLE);
    autoTable(doc, {
      startY: y,
      head: [["Category", "Total NC", "Completion NC", "Pending", "Completion %", "Current Score", "Max Score"]],
      body: intAudit.map((r) => [
        `Category ${r.category}`,
        r.totalNC ?? "N/A",
        r.completionNC ?? "N/A",
        r.pending ?? "N/A",
        r.completionPercent != null ? `${r.completionPercent.toFixed(2)}%` : "N/A",
        r.currentScore != null ? r.currentScore.toFixed(2) : "N/A",
        r.maxScore,
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: PURPLE },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  // Fire safety parameters
  const params: FireSafetyParameter[] = scorecard.fireSafetyParameters || [];
  if (params.length) {
    if (y > doc.internal.pageSize.height - 60) { doc.addPage(); pageHeader(doc, premiseName, "Fire Safety Scorecard — Parameters", month, year); y = 22; }
    y = sectionBand(doc, "PARAMETERS OF FIRE SAFETY", y, TEAL);
    const totalScore = params.reduce((s, p) => s + (p.score ?? 0), 0);
    const totalMax = params.reduce((s, p) => s + p.maxScore, 0);
    autoTable(doc, {
      startY: y,
      head: [["Parameter", "In %", "Score", "Max Score", "Percentage"]],
      body: [
        ...params.map((p) => [
          p.parameter,
          p.inPercent != null ? p.inPercent.toFixed(2) : "N/A",
          p.score != null ? p.score.toFixed(2) : "N/A",
          p.maxScore.toFixed(2),
          p.percentage != null ? `${p.percentage.toFixed(2)}%` : "N/A",
        ]),
        ["TOTAL", "", totalScore.toFixed(2), totalMax.toFixed(2),
          totalMax > 0 ? `${((totalScore / totalMax) * 100).toFixed(2)}%` : "N/A"],
      ],
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: TEAL },
      columnStyles: { 0: { cellWidth: 120 } },
      didParseCell: (data) => {
        if (data.row.index === params.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [255, 235, 150];
        }
      },
    });
  }

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ─── Electrical Safety Scorecard PDF ─────────────────────────────────────────

function generateElectricalPdf(
  scorecard: any,
  premiseName: string,
  month: number,
  year: number
): Uint8Array {
  const doc = new jsPDF({ orientation: "landscape" });
  pageHeader(doc, premiseName, "Electrical Safety Scorecard", month, year);

  const sections: ElectricalSafetySection[] = scorecard.sections || [];
  const overallScore = sections.reduce((s, sec) => s + sec.totalScore, 0);
  const overallMax = sections.reduce((s, sec) => s + sec.totalMaxScore, 0);
  const overallPct = overallMax > 0 ? (overallScore / overallMax) * 100 : 0;

  let y = 22;
  doc.setFontSize(10);
  doc.setTextColor(...GREY_TEXT);
  doc.text(
    `Name: ${scorecard.name || "N/A"}    Date: ${scorecard.date || "N/A"}    Premises: ${premiseName}`,
    14,
    y
  );
  y += 6;
  doc.text(
    `Overall Score: ${overallScore.toFixed(2)} / ${overallMax} (${overallPct.toFixed(2)}%)`,
    14,
    y
  );
  y += 8;

  sections.forEach((section) => {
    if (y > doc.internal.pageSize.height - 40) {
      doc.addPage();
      pageHeader(doc, premiseName, "Electrical Safety Scorecard", month, year);
      y = 22;
    }
    y = sectionBand(
      doc,
      `${section.name}  |  Score: ${section.totalScore.toFixed(2)} / ${section.totalMaxScore} (${section.percentage.toFixed(2)}%)`,
      y,
      BLUE
    );
    autoTable(doc, {
      startY: y,
      head: [["#", "Item Description", "Compliance Status", "Score", "Max Score", "Remarks"]],
      body: section.items.map((item, idx) => [
        idx + 1,
        item.description,
        item.complianceStatus || "N/A",
        item.score.toFixed(2),
        item.maxScore,
        item.remarks || "—",
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: BLUE },
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 110 }, 2: { cellWidth: 30 }, 3: { cellWidth: 14 }, 4: { cellWidth: 14 } },
      didDrawPage: () => { y = 22; },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  });

  return doc.output("arraybuffer") as unknown as Uint8Array;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  premises: premisesApiResponse[];
}

const AllPremisesReportDownload: React.FC<Props> = ({ premises }) => {
  const [open, setOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-indexed
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; label: string } | null>(null);

  const handleDownload = useCallback(async () => {
    if (!premises.length || generating) return;
    setGenerating(true);
    const month = selectedMonth + 1; // 1-indexed for API
    const year = selectedYear;
    const total = premises.length;

    try {
      const zip = new JSZip();

      // Fetch all data once (limit 2000) — we filter per-premise in the browser
      setProgress({ done: 0, total, label: "Fetching data..." });

      const [allMIS, allFireSC, allElecSC] = await Promise.all([
        safeFetchAll("/api/fire-safety-mis-checklists", { month, year }),
        safeFetchAll("/api/fire-safety-scorecards", { month, year }),
        safeFetchAll("/api/electrical-safety-scorecards", { month, year }),
      ]);

      for (let i = 0; i < premises.length; i++) {
        const premise = premises[i];
        const premiseId = String((premise as any)._id || (premise as any).id || "");
        const premiseName = premise.premisesName || premiseId || "unknown";
        const folderName = safeName(premiseName || premiseId);

        setProgress({ done: i, total, label: `Generating reports for ${premiseName}...` });

        const folder = zip.folder(folderName)!;

        // ── MIS Report ────────────────────────────────────────────────────────
        const misRecord = pickRecordForMonth(allMIS, premiseId, month, year);
        if (misRecord) {
          try {
            const pdfBytes = generateMISPdf(misRecord, premiseName, month, year);
            folder.file(`MIS_Report_${MONTH_NAMES[month - 1]}_${year}.pdf`, pdfBytes);
          } catch (e) {
            console.warn(`MIS PDF failed for ${premiseName}:`, e);
          }
        }

        // ── Fire Safety Scorecard ─────────────────────────────────────────────
        const fireSCRecord = pickRecordForMonth(allFireSC, premiseId, month, year);
        if (fireSCRecord) {
          try {
            const pdfBytes = generateFireScorecardPdf(fireSCRecord, premiseName, month, year);
            folder.file(`Fire_Safety_Scorecard_${MONTH_NAMES[month - 1]}_${year}.pdf`, pdfBytes);
          } catch (e) {
            console.warn(`Fire scorecard PDF failed for ${premiseName}:`, e);
          }
        }

        // ── Electrical Safety Scorecard ───────────────────────────────────────
        const elecSCRecord = pickRecordForMonth(allElecSC, premiseId, month, year);
        if (elecSCRecord) {
          try {
            const pdfBytes = generateElectricalPdf(elecSCRecord, premiseName, month, year);
            folder.file(`Electrical_Safety_Scorecard_${MONTH_NAMES[month - 1]}_${year}.pdf`, pdfBytes);
          } catch (e) {
            console.warn(`Electrical scorecard PDF failed for ${premiseName}:`, e);
          }
        }
      }

      setProgress({ done: total, total, label: "Compressing ZIP..." });

      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      saveAs(blob, `All_Premises_Reports_${MONTH_NAMES[month - 1]}_${year}.zip`);
      setOpen(false);
    } catch (err) {
      console.error("Bulk report generation failed:", err);
    } finally {
      setGenerating(false);
      setProgress(null);
    }
  }, [premises, selectedMonth, selectedYear, generating]);

  const yearOptions = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i);

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 border-2 border-orange-500 text-orange-600 rounded-lg hover:bg-orange-500 hover:text-white transition-colors text-sm font-semibold"
      >
        <Download className="w-4 h-4" />
        Reports of all premises
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !generating && setOpen(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-orange-500 px-6 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Download className="w-5 h-5 text-white" />
                <h3 className="text-white font-semibold text-base">Reports of All Premises</h3>
              </div>
              {!generating && (
                <button
                  onClick={() => setOpen(false)}
                  className="text-white/80 hover:text-white transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Body */}
            <div className="px-6 py-4 flex flex-col gap-4">
              <p className="text-sm text-gray-500 leading-relaxed">
                Download all reports for{" "}
                <span className="font-semibold text-gray-700">
                  all {premises.length} premise{premises.length !== 1 ? "s" : ""}
                </span>{" "}
                packed in a ZIP file. Each premise gets its own folder.
              </p>

              {/* Month & Year picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Month &amp; Year
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    disabled={generating}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent appearance-none bg-white disabled:opacity-50"
                  >
                    {MONTH_NAMES.map((m, idx) => (
                      <option key={idx} value={idx}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    disabled={generating}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent appearance-none bg-white disabled:opacity-50"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Premises with no data for the selected month will be skipped (no empty folders).
                </p>
              </div>

              {/* Included reports info */}
              <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                  Reports included per premise
                </p>
                {[
                  { dot: "bg-blue-500", text: "MIS Report — all tables including audit tables (I–V)" },
                  { dot: "bg-orange-400", text: "Fire Safety Scorecard — audit tables + parameters" },
                  { dot: "bg-teal-500", text: "Electrical Safety Scorecard — all sections" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${item.dot}`} />
                    <span className="text-xs text-gray-500">{item.text}</span>
                  </div>
                ))}
                <p className="text-xs text-gray-400 mt-1">
                  ZIP folder structure: <code className="bg-gray-200 px-1 rounded">PremiseName/Report.pdf</code>
                </p>
              </div>

              {/* Progress bar */}
              {generating && progress && (
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span className="truncate max-w-[75%]">{progress.label}</span>
                    <span>{Math.round((progress.done / progress.total) * 100)}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(progress.done / progress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Generating PDFs... this may take a moment.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 pb-5 flex items-center justify-end gap-3">
              <button
                onClick={() => setOpen(false)}
                disabled={generating}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={handleDownload}
                disabled={generating || premises.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download ZIP
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AllPremisesReportDownload;
